import {
  Dispatch,
  FunctionComponent,
  SetStateAction,
  memo,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { ChannelContext } from '../../../../context';
import { Event, EventStatus, User, useLazyEventsQuery } from '../../../../services';
import { useTags } from '../../../hooks';
import { getCssVar } from '../../../utils';
import { AutoComplete } from '../../AutoComplete';
import { CloseButton } from '../../CloseButton';
import { EventItemPreview } from '../../EventItem';
import { IconLabel } from '../../IconLabel';
import { TextAreaField } from '../../TextAreaField';
import { UserMentionPreview } from '../../UserMentionPreview';
import { CreateCardSettingsTabType, EventPostFields } from '../CreateCardModal';

import classNames from 'classnames';
import classes from './EventPost.module.scss';

interface EventPostProps {
  eventPostFields: EventPostFields;
  setEventPostFields: Dispatch<SetStateAction<EventPostFields>>;
  mentionedUsers: User[];
  setUserMentionModalOpen: Dispatch<SetStateAction<boolean>>;
  postInStoryAllowMentions: boolean;
  setSettingsOpen: (isOpen: boolean) => void;
  setActiveEventPostSettingsTabIndex: (index: number) => void;
}

export const EventPost: FunctionComponent<EventPostProps> = memo(
  ({
    eventPostFields,
    setEventPostFields,
    mentionedUsers,
    setUserMentionModalOpen,
    postInStoryAllowMentions,
    setSettingsOpen,
    setActiveEventPostSettingsTabIndex,
  }) => {
    const { t } = useTranslation();

    const { hasContentTags } = useTags();

    const { id: channelId } = useContext(ChannelContext).channel;

    const [fetchEvents] = useLazyEventsQuery();

    const headlineRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
      const input = headlineRef.current;

      if (!input) {
        return;
      }

      input.setSelectionRange(input.value.length, input.value.length);

      setTimeout(() => input.focus(), 0);
    }, []);

    const { event, headline } = eventPostFields;

    const eventsFetchQuery = useCallback(
      (query: string, page?: number) =>
        fetchEvents({
          channelId,
          page,
          filters: { query, statuses: [EventStatus.PUBLISHED] },
        }),
      [channelId, fetchEvents]
    );

    const onEventChange = useCallback(
      (events: Event[]) => {
        setEventPostFields((eventPostFieldsPrev) => ({ ...eventPostFieldsPrev, event: events[0] }));
      },
      [setEventPostFields]
    );

    const onHeadlineChange = useCallback(
      (headline: string) => {
        setEventPostFields((eventPostFieldsPrev) => ({ ...eventPostFieldsPrev, headline }));
      },
      [setEventPostFields]
    );

    const handleClearEvent = useCallback(() => {
      setEventPostFields((eventPostFieldsPrev) => ({ ...eventPostFieldsPrev, event: null }));
    }, [setEventPostFields]);

    const eventItemTemplate = useCallback(
      ({ name }: Event) => <span className={classes['event-post__item-name']}>{name}</span>,
      []
    );

    const tagsClickHandler = useCallback(() => {
      setActiveEventPostSettingsTabIndex(CreateCardSettingsTabType.TAGS);
      setSettingsOpen(true);
    }, [setActiveEventPostSettingsTabIndex, setSettingsOpen]);

    return (
      <div
        className={classNames(classes['event-post'], {
          [classes['event-post--mobile']]: isMobileOnly,
        })}
      >
        <div
          className={classNames(classes['event-post__scrollable'], {
            [classes['event-post__scrollable--mobile']]: isMobileOnly,
          })}
        >
          <TextAreaField
            ref={headlineRef}
            inputClassName={classes['event-post__headline']}
            placeholder={t('addContent.event-post-comment')}
            value={headline}
            onChange={({ target: { value } }) => onHeadlineChange(value)}
          />
          <UserMentionPreview
            mentionedUsers={mentionedUsers}
            {...(postInStoryAllowMentions && { onClick: () => setUserMentionModalOpen(true) })}
          />
          {!event && (
            <div className={classes['event-post__autocomplete-wrapper']}>
              <AutoComplete
                propToIdentify={'id'}
                propToDisplay={'name'}
                value={[]}
                fetchQuery={eventsFetchQuery}
                itemTemplate={eventItemTemplate}
                placeholder={t('addContent.event-post-search')}
                onChange={onEventChange}
                listMaxHeightRem={10.5}
                singleSelect
                closeOnSelect
              />
              <div className={classes['event-post__autocomplete-hint']}>
                {t('addContent.event-post-search-hint')}
              </div>
            </div>
          )}
          {event && (
            <div className={classes['event-post__selected']}>
              <EventItemPreview event={event} />
              <CloseButton
                onClick={handleClearEvent}
                ariaLabel={t('common.remove')}
                className={classes['event-post__close']}
              />
            </div>
          )}
        </div>
        <div className={classes['event-post__pane']}>
          <div className={classes['event-post__pane-fields']}>
            {postInStoryAllowMentions && (
              <IconLabel
                iconId={'mention'}
                iconSize={20}
                label={t('addContent.mention')}
                color={getCssVar('--create-card-link-color')}
                hoverColor={getCssVar('--create-card-link-hover-color')}
                onClick={() => setUserMentionModalOpen(true)}
              />
            )}
            {hasContentTags && (
              <IconLabel
                iconId={'tags'}
                iconSize={20}
                label={t('addContent.tags')}
                color={getCssVar('--create-card-link-color')}
                hoverColor={getCssVar('--create-card-link-hover-color')}
                onClick={tagsClickHandler}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
);
